import React, { useState, useEffect } from 'react';
import './styles/App.css';
import InitialQuestions from './components/InitialQuestions';
import RoleSelection from './components/RoleSelection';
import StoryScene from './components/StoryScene';
import EndScreen from './components/EndScreen';
import ErrorMessage from './components/ErrorMessage';
import Logo from './components/Logo';
import DebugPage from './components/DebugPage';
import { generateScenario, generateScene } from './api/scenarioApi';

const App = () => {
  const [stage, setStage] = useState('questions'); // questions, roles, story, end, debug
  const [answers, setAnswers] = useState(['', '', '']);
  const [scenario, setScenario] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [scenes, setScenes] = useState([]);
  const [userChoices, setUserChoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);

  const questions = [
    'Tell me one daily task that most people let AI do for them in 2035. How does it make life better?',
    'In one sentence, say how a typical adult earns money in 2035.',
    'In one sentence, what is the biggest public debate about AI use in 2035, and which side is winning?'
  ];

  const handleAnswerChange = (index, text) => {
    const newAnswers = [...answers];
    newAnswers[index] = text;
    setAnswers(newAnswers);
  };

  // Helper function to scroll to the top of the content
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const scenarioData = await generateScenario(questions, answers);
      setScenario(scenarioData);
      setStage('roles');
      // Scroll to top after transition
      scrollToTop();
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to generate your scenario. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role) => {
    console.log("Role selected:", role);
    
    // Store the role first
    setSelectedRole(role);
    
    // Show loading state immediately by setting stage to 'story' and loading to true
    setStage('story');
    setLoading(true);
    
    // Then generate the scene with the selected role directly
    const generateSceneWithRole = async () => {
      try {
        setError(null);
        
        console.log("Generating scene with selected role:", role);
        
        if (!role) {
          setError("No role was selected. Please try again.");
          setLoading(false);
          return;
        }
        
        // For the first scene, we need to ensure we have the right structure
        const firstSceneAttempt = scenes.length === 0;
        
        // Use the role parameter directly instead of relying on state
        const sceneData = await generateScene(scenario, role, scenes, userChoices);
        console.log("Received scene data:", sceneData);
        
        // Check if we got valid scene data
        if (!sceneData || (!sceneData.scene_text && !sceneData.choice1)) {
          throw new Error("Invalid scene data received from API");
        }
        
        const defaultText = "Scene text not available";
        const defaultChoice = "Continue...";
        
        // Create new scene with both the text and all possible choices
        const newScene = {
          text: sceneData.scene_text || defaultText,
          choices: [
            sceneData.choice1 || defaultChoice,
            sceneData.choice2 || defaultChoice,
            sceneData.choice3 || defaultChoice
          ]
        };
        
        // Store the new scene with all its choices
        setScenes([...scenes, newScene]);
        
        setCurrentSceneIndex(currentSceneIndex + 1);
        
        // Scroll to top to show the first scene
        scrollToTop();
      } catch (err) {
        console.error('Error generating scene:', err);
        setError(`Failed to generate your story: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    // Call the function immediately
    generateSceneWithRole();
  };

  // This function is now only used for retrying after an error
  const generateNextScene = async () => {
    setLoading(true);
    setError(null);
    
    console.log("Retrying scene generation with:", {
      scenario,
      selectedRole,
      sceneCount: scenes.length,
      choiceCount: userChoices.length,
      userChoices
    });
    
    // Double-check that we have all needed data
    if (!scenario) {
      setError("Missing scenario data");
      setLoading(false);
      return;
    }
    
    if (!selectedRole) {
      setError("No role selected");
      setLoading(false);
      return;
    }
    
    try {
      const sceneData = await generateScene(scenario, selectedRole, scenes, userChoices);
      console.log("Received scene data:", sceneData);
      
      // Check if we got valid scene data
      if (!sceneData || (!sceneData.scene_text && !sceneData.choice1)) {
        throw new Error("Invalid scene data received from API");
      }
      
      const defaultText = "Scene text not available";
      const defaultChoice = "Continue...";
      
      // Create new scene with both the text and all possible choices
      const newScene = {
        text: sceneData.scene_text || defaultText,
        choices: [
          sceneData.choice1 || defaultChoice,
          sceneData.choice2 || defaultChoice,
          sceneData.choice3 || defaultChoice
        ]
      };
      
      // Store the new scene
      setScenes([...scenes, newScene]);
      setCurrentSceneIndex(currentSceneIndex + 1);
      
      // Scroll to top to show the new scene
      scrollToTop();
    } catch (err) {
      console.error('Error generating scene:', err);
      setError(`Failed to generate the next part of your story: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChoice = (choice) => {
    console.log(`User selected choice: ${choice} for scene ${currentSceneIndex}`);
    
    // Store the user's choice first
    const updatedChoices = [...userChoices, choice];
    
    // Check if we're at the 11th scene (index 10), as the 12th scene will be the final one
    if (currentSceneIndex < 11) {
      // Generate the next scene with the updated choices directly
      // instead of relying on state updates
      generateNextSceneWithChoice(choice, updatedChoices);
    } else {
      // Update state and end the story
      setUserChoices(updatedChoices);
      setStage('end');
    }
  };
  
  // New function to generate next scene using the choice directly
  const generateNextSceneWithChoice = async (latestChoice, allChoices) => {
    setLoading(true);
    setError(null);
    
    console.log("Generating next scene with latest choice:", latestChoice);
    console.log("All choices so far:", allChoices);
    
    try {
      // Use the current scenes plus the latest choice
      const sceneData = await generateScene(scenario, selectedRole, scenes, allChoices);
      console.log("Received scene data:", sceneData);
      
      // Check if we got valid scene data
      if (!sceneData || (!sceneData.scene_text && !sceneData.choice1)) {
        throw new Error("Invalid scene data received from API");
      }
      
      const defaultText = "Scene text not available";
      const defaultChoice = "Continue...";
      
      // Create new scene with both the text and all possible choices
      const newScene = {
        text: sceneData.scene_text || defaultText,
        choices: [
          sceneData.choice1 || defaultChoice,
          sceneData.choice2 || defaultChoice,
          sceneData.choice3 || defaultChoice
        ]
      };
      
      // Update all state at once to ensure consistency
      setUserChoices(allChoices);
      setScenes([...scenes, newScene]);
      setCurrentSceneIndex(currentSceneIndex + 1);
      
      // Scroll to top to show the new scene
      scrollToTop();
    } catch (err) {
      console.error('Error generating scene:', err);
      setError(`Failed to generate the next part of your story: ${err.message}`);
      
      // Still update the choices even if there was an error
      setUserChoices(allChoices);
    } finally {
      setLoading(false);
    }
  };

  const resetToStart = () => {
    setStage('questions');
    setAnswers(['', '', '']);
    setScenario(null);
    setSelectedRole(null);
    setScenes([]);
    setUserChoices([]);
    setCurrentSceneIndex(0);
    scrollToTop();
  };

  const resetToRoles = () => {
    setStage('roles');
    setSelectedRole(null);
    setScenes([]);
    setUserChoices([]);
    setCurrentSceneIndex(0);
    scrollToTop();
  };
  
  const goToDebugPage = () => {
    setStage('debug');
  };
  
  // Check for debug mode in URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === 'true') {
      goToDebugPage();
    }
  }, []);

  // Return to main app from debug page
  const returnToMain = () => {
    setStage('questions');
  };

  // Render debug page if in debug mode
  if (stage === 'debug') {
    return <DebugPage onReturn={returnToMain} />;
  }

  return (
    <div className="app">
      <Logo size={60} />
      <h1 className="app-title">2035: FUTURE PATHS</h1>
      <p className="app-subtitle">Explore possible futures in a world shaped by AI</p>

      {/* Show error message if there is one, but continue to show the current stage */}
      {error && (
        <ErrorMessage 
          message={error} 
          onRetry={() => {
            setError(null);
            if (stage === 'questions') fetchRoles();
            if (stage === 'story') generateNextScene();
          }} 
        />
      )}

      {/* Always show the current stage, even if there's an error */}
      {stage === 'questions' && (
        <InitialQuestions 
          questions={questions} 
          answers={answers} 
          onAnswerChange={handleAnswerChange} 
          onSubmit={fetchRoles} 
          loading={loading}
        />
      )}

      {stage === 'roles' && (
        <RoleSelection 
          scenario={scenario} 
          onRoleSelect={handleRoleSelect} 
        />
      )}

      {stage === 'story' && (
        <StoryScene 
          scene={scenes.length > 0 ? scenes[scenes.length - 1] : null} 
          sceneNumber={scenes.length} 
          onChoice={handleChoice}
          loading={loading}
          isFinalScene={currentSceneIndex >= 11}
          selectedRole={selectedRole}
        />
      )}

      {stage === 'end' && (
        <EndScreen 
          onNewScenario={resetToStart}
          onNewRole={resetToRoles}
          scenes={scenes}
          userChoices={userChoices}
        />
      )}
    </div>
  );
};

export default App;
